import React from 'react';
import { FeedSuggestionsRailSkeleton } from '../../../newsintelligence/support/openDiscussions/components/feed/FeedSuggestionsRail';
import './RouteSkeletons.css';

function Bone({ className = '' }) {
  return <span className={`route-skeleton-bone ${className}`.trim()} aria-hidden />;
}

export function AuthRouteSkeleton() {
  return (
    <div className="route-skeleton route-skeleton--auth" aria-busy="true" aria-label="Loading">
      <div className="route-skeleton-auth-card">
        <Bone className="route-skeleton-bone--circle-lg" />
        <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
        <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
      </div>
    </div>
  );
}

export function PostsFeedRouteSkeleton({ rows = 4, showSuggestionsRail = true }) {
  return (
    <div className="support-open-disc route-skeleton route-skeleton--feed" aria-busy="true" aria-label="Loading posts">
      <div className="route-skeleton-feed-compose route-skeleton-feed-compose--posts">
        <Bone className="route-skeleton-bone--circle route-skeleton-bone--compose-avatar" />
        <Bone className="route-skeleton-bone--line route-skeleton-bone--line-compose" />
        <Bone className="route-skeleton-bone--pill route-skeleton-bone--feed-filter" />
        <Bone className="route-skeleton-bone--pill route-skeleton-bone--feed-filter" />
        <Bone className="route-skeleton-bone--pill route-skeleton-bone--feed-filter" />
      </div>
      {showSuggestionsRail ? <FeedSuggestionsRailSkeleton /> : null}
      <ul className="route-skeleton-feed-list" aria-hidden>
        {Array.from({ length: rows }).map((_, i) => (
          <li key={i} className="route-skeleton-feed-card">
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-xs" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-lg" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** App-route fallback: mobile shell chrome + feed skeleton so the first paint matches PostsPage. */
export function PostsFeedPageRouteSkeleton({ rows = 4 }) {
  return (
    <div className="route-skeleton route-skeleton--posts-page" aria-busy="true" aria-label="Loading posts">
      <div className="route-skeleton-mobile-chrome" aria-hidden>
        <span className="route-skeleton-mobile-chrome-left">
          <Bone className="route-skeleton-bone--icon-btn" />
          <Bone className="route-skeleton-bone--logo-icon" />
          <Bone className="route-skeleton-bone--brand-line" />
        </span>
        <span className="route-skeleton-mobile-chrome-right">
          <Bone className="route-skeleton-bone--icon-btn" />
          <Bone className="route-skeleton-bone--icon-btn" />
        </span>
      </div>
      <PostsFeedRouteSkeleton rows={rows} />
    </div>
  );
}

export function NotificationsTimelineSkeleton({ rows = 6 }) {
  return (
    <div className="route-skeleton route-skeleton--notifications" aria-busy="true" aria-label="Loading notifications">
      <div className="route-skeleton-notifications-header">
        <Bone className="route-skeleton-bone--line route-skeleton-bone--line-title" />
        <Bone className="route-skeleton-bone--line route-skeleton-bone--line-tabs" />
      </div>
      <ul className="route-skeleton-notifications-list">
        {Array.from({ length: rows }).map((_, i) => (
          <li key={i} className="route-skeleton-notifications-item">
            <Bone className="route-skeleton-bone--circle" />
            <span className="route-skeleton-notifications-body">
              <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
              <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AdminPanelRouteSkeleton() {
  return (
    <div className="route-skeleton route-skeleton--admin" aria-busy="true" aria-label="Loading admin panel">
      <Bone className="route-skeleton-bone--line route-skeleton-bone--line-title" />
      <div className="route-skeleton-admin-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="route-skeleton-admin-card">
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-lg" />
          </div>
        ))}
      </div>
      <div className="route-skeleton-admin-table">
        {Array.from({ length: 5 }).map((_, i) => (
          <Bone key={i} className="route-skeleton-bone--line route-skeleton-bone--line-table" />
        ))}
      </div>
    </div>
  );
}

export function PublicProfileSkeleton() {
  return (
    <div className="support-open-disc public-profile-page route-skeleton route-skeleton--profile" aria-busy="true" aria-label="Loading profile">
      <Bone className="route-skeleton-bone--cover" />
      <div className="route-skeleton-profile-hero">
        <Bone className="route-skeleton-bone--circle-lg" />
        <div className="route-skeleton-profile-copy">
          <Bone className="route-skeleton-bone--line route-skeleton-bone--line-title" />
          <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
          <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
        </div>
      </div>
      <ul className="route-skeleton-feed-list">
        {Array.from({ length: 3 }).map((_, i) => (
          <li key={i} className="route-skeleton-feed-card">
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-lg" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PeopleListSkeleton({ rows = 5, className = '' }) {
  return (
    <ul className={`route-skeleton-people-list ${className}`.trim()} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="route-skeleton-people-row">
          <Bone className="route-skeleton-bone--circle" />
          <span className="route-skeleton-people-body">
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
            <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" />
          </span>
        </li>
      ))}
    </ul>
  );
}

export function GalleryGridSkeleton({ tiles = 6 }) {
  return (
    <ul className="route-skeleton-gallery-grid" aria-busy="true" aria-label="Loading gallery">
      {Array.from({ length: tiles }).map((_, i) => (
        <li key={i}>
          <Bone className="route-skeleton-bone--gallery-tile" />
        </li>
      ))}
    </ul>
  );
}

export function InlineListSkeleton({ rows = 4 }) {
  return (
    <div className="route-skeleton-inline-list" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Bone key={i} className="route-skeleton-bone--line route-skeleton-bone--line-table" />
      ))}
    </div>
  );
}

/** StorageBox sidebar folder tree */
export function StorageBoxSidebarTreeSkeleton({ rows = 5 }) {
  return (
    <ul className="route-skeleton-sb-tree" aria-busy="true" aria-label="Loading folders">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className="route-skeleton-sb-tree-row"
          style={{ paddingLeft: `${(i % 2) * 0.65}rem` }}
        >
          <Bone className="route-skeleton-bone--sb-tree-toggle" />
          <Bone className="route-skeleton-bone--sb-tree-icon" />
          <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md route-skeleton-bone--sb-tree-label" />
        </li>
      ))}
    </ul>
  );
}

export function StorageBoxQuotaCardSkeleton() {
  return (
    <div className="route-skeleton-sb-quota sb-quota-card" aria-busy="true" aria-label="Loading storage usage">
      <Bone className="route-skeleton-bone--line route-skeleton-bone--line-xs" />
      <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
      <Bone className="route-skeleton-bone--sb-quota-bar" />
    </div>
  );
}

/** Main StorageBox file list / grid while folder contents load */
export function StorageBoxWorkspaceSkeleton({ variant = 'grid', rows = 8, tiles = 12 }) {
  return (
    <div className="route-skeleton route-skeleton--storagebox-workspace" aria-busy="true" aria-label="Loading files">
      {variant === 'grid' ? (
        <ul className="route-skeleton-sb-grid">
          {Array.from({ length: tiles }).map((_, i) => (
            <li key={i}>
              <Bone className="route-skeleton-bone--sb-grid-tile" />
              <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm route-skeleton-bone--sb-grid-caption" />
            </li>
          ))}
        </ul>
      ) : (
        <div className="route-skeleton-sb-table">
          <Bone className="route-skeleton-bone--line route-skeleton-bone--sb-table-head" />
          {Array.from({ length: rows }).map((_, i) => (
            <Bone key={i} className="route-skeleton-bone--line route-skeleton-bone--line-table" />
          ))}
        </div>
      )}
    </div>
  );
}

/** Full-route placeholder while StorageBox session is validated (avoids /signin flash). */
export function StorageBoxRouteSkeleton() {
  return (
    <div
      className="route-skeleton route-skeleton--storagebox-route posts-page-layout posts-page-layout--storagebox"
      aria-busy="true"
      aria-label="Loading StorageBox"
    >
      <div className="route-skeleton-sb-route-layout">
        <aside className="route-skeleton-sb-route-sidebar" aria-hidden>
          <StorageBoxQuotaCardSkeleton />
          <StorageBoxSidebarTreeSkeleton rows={6} />
        </aside>
        <main className="route-skeleton-sb-route-main">
          <StorageBoxWorkspaceSkeleton variant="grid" tiles={10} />
        </main>
      </div>
    </div>
  );
}

/** StorageBox details / overview panel */
export function StorageBoxPreviewSkeleton({ compact = false }) {
  return (
    <div
      className={`route-skeleton-sb-preview${compact ? ' route-skeleton-sb-preview--compact' : ''}`}
      aria-busy="true"
      aria-label="Loading details"
    >
      <Bone className="route-skeleton-bone--circle-lg route-skeleton-sb-preview-icon" />
      <Bone className="route-skeleton-bone--line route-skeleton-bone--line-title" />
      <Bone className="route-skeleton-bone--line route-skeleton-bone--line-md" />
      {!compact ? <Bone className="route-skeleton-bone--line route-skeleton-bone--line-sm" /> : null}
      <InlineListSkeleton rows={compact ? 3 : 5} />
    </div>
  );
}
